import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Alert } from '@mui/material';
import { Icon } from '@iconify/react';
interface RTGSActionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => void;
  transaction: {
    id: string;
    mtoName: string;
    amountFCY: string;
    currencyFCY: string;
  };
  action: 'send' | 'reverse' | 'settle';
}
const RTGSActionDialog: React.FC<RTGSActionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  transaction,
  action
}) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCommentRequired = action === 'reverse' || action === 'settle';
  const handleConfirm = async () => {
    if (isCommentRequired && !notes.trim()) {
      setError('Comment is required when marking transaction as success or failed');
      return;
    }
    setLoading(true);
    setError(null);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onConfirm(notes);
    setLoading(false);
    setNotes('');
  };
  const handleClose = () => {
    setNotes('');
    setError(null);
    onClose();
  };
  const actionConfig = {
    send: {
      title: 'Move to RTGS',
      icon: 'lucide:arrow-right-circle',
      color: '#8B5CF6',
      description: 'This transaction will be sent to the bank for RTGS processing.',
      confirmLabel: 'Send to Bank'
    },
    reverse: {
      title: 'Mark as Reversed (Failed)',
      icon: 'lucide:undo-2',
      color: '#EF4444',
      description: 'Mark this RTGS transaction as reversed (failed). A comment is required.',
      confirmLabel: 'Mark as Reversed'
    },
    settle: {
      title: 'Mark as Manually Settled (Success)',
      icon: 'lucide:check-circle',
      color: '#10B981',
      description: 'Mark this RTGS transaction as manually settled (success). A comment is required.',
      confirmLabel: 'Mark as Settled'
    }
  };
  const config = actionConfig[action];
  return <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-id="element-1781">
      <DialogTitle data-id="element-1782">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-1783">
          <Icon icon={config.icon} width={24} height={24} color={config.color} data-id="element-1784" />
          <Typography variant="h6" fontWeight={600} data-id="element-1785">
            {config.title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent data-id="element-1786">
        <Alert severity={action === 'send' ? 'info' : 'warning'} sx={{
        mb: 3
      }} data-id="element-1787">
          {config.description}
        </Alert>

        <Box sx={{
        mb: 3
      }} data-id="element-1788">
          <Typography variant="subtitle2" gutterBottom data-id="element-1789">
            Transaction Details
          </Typography>
          <Box sx={{
          p: 2,
          backgroundColor: '#F8FAFC',
          borderRadius: 1,
          border: '1px solid #E5E7EB'
        }} data-id="element-1790">
            <Typography variant="body2" data-id="element-1791">
              <strong data-id="element-1792">Transaction ID:</strong> {transaction.id}
            </Typography>
            <Typography variant="body2" data-id="element-1793">
              <strong data-id="element-1794">MTO:</strong> {transaction.mtoName}
            </Typography>
            <Typography variant="body2" data-id="element-1795">
              <strong data-id="element-1796">Amount:</strong> {transaction.currencyFCY}{' '}
              {transaction.amountFCY}
            </Typography>
          </Box>
        </Box>

        <TextField fullWidth multiline rows={4} label={isCommentRequired ? 'Comment (Required)' : 'Notes (Optional)'} placeholder={isCommentRequired ? 'Enter mandatory comment...' : 'Add any additional notes or comments...'} value={notes} onChange={e => {
        setNotes(e.target.value);
        if (error) setError(null);
      }} required={isCommentRequired} error={!!error} helperText={error} data-id="element-1797" />
      </DialogContent>
      <DialogActions sx={{
      p: 3,
      pt: 0
    }} data-id="element-1798">
        <Button onClick={handleClose} disabled={loading} data-id="element-1799">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || isCommentRequired && !notes.trim()} sx={{
        backgroundColor: config.color,
        '&:hover': {
          backgroundColor: config.color,
          opacity: 0.9
        }
      }} data-id="element-1800">
          {loading ? 'Processing...' : config.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>;
};
export default RTGSActionDialog;